"use client";

import React from "react";
import { AlertCircle, X, Copy } from "lucide-react";
import { Button } from "../../button";
import { Badge } from "../../badge";
import { ERROR_EXPLANATIONS, copyToClipboard, parseError } from "./utils";

interface EnhancedErrorDisplayProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  error: any;
  onDismiss?: () => void;
  showDetails?: boolean;
  title?: string;
  className?: string;
}

export const EnhancedErrorDisplay: React.FC<EnhancedErrorDisplayProps> = ({
  error,
  onDismiss,
  showDetails = false,
  title = "Error",
  className = "",
}) => {
  const [showFullDetails, setShowFullDetails] = React.useState(false);
  const [copySuccess, setCopySuccess] = React.useState(false);

  const parsedError = parseError(error);
  const errorExplanation = parsedError.code ? ERROR_EXPLANATIONS[parsedError.code] : null;

  const handleCopyError = () => {
    const errorText = `Error: ${parsedError.message}\nCode: ${parsedError.code || "N/A"}\nDetails: ${parsedError.details || "N/A"}`;
    copyToClipboard(errorText);
    setCopySuccess(true);
    setTimeout(() => setCopySuccess(false), 2000);
  };

  return (
    <div className={`border-l-4 border-red-500 bg-red-50 dark:bg-red-950/40 p-4 rounded-r-lg shadow-sm ${className}`}>
      <div className="flex items-start">
        <div className="flex-shrink-0">
          <AlertCircle className="h-5 w-5 text-red-600" />
        </div>
        <div className="ml-3 flex-1">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-red-800">
              {errorExplanation?.title || title}
            </h3>
            <div className="flex items-center gap-2">
              {parsedError.code && (
                <Badge variant="outline" className="text-xs bg-red-100 border-red-300 text-red-700 dark:bg-red-900/40 dark:border-red-800 dark:text-red-300">
                  {parsedError.code}
                </Badge>
              )}
              {onDismiss && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onDismiss}
                  className="h-6 w-6 p-0 text-red-600 hover:text-red-800 hover:bg-red-100 dark:text-red-400 dark:hover:text-red-200 dark:hover:bg-red-900/40"
                >
                  <X size={14} />
                </Button>
              )}
            </div>
          </div>

          <div className="mt-2">
            <p className="text-sm text-red-700">
              {errorExplanation?.description || parsedError.message}
            </p>

            {errorExplanation?.suggestion && (
              <div className="mt-3 p-3 bg-red-100 rounded-md border border-red-200 dark:bg-red-900/40 dark:border-red-800">
                <p className="text-sm text-red-800">
                  <strong>💡 Suggestion:</strong> {errorExplanation.suggestion}
                </p>
              </div>
            )}

            {parsedError.field && (
              <p className="text-xs text-red-600 mt-2">
                <strong>Field:</strong> {parsedError.field}
              </p>
            )}

            {showDetails && (parsedError.details || parsedError.hint || showFullDetails) && (
              <div className="mt-3">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowFullDetails(!showFullDetails)}
                  className="text-xs text-red-600 hover:text-red-800 hover:bg-red-100 p-1 dark:text-red-400 dark:hover:text-red-200 dark:hover:bg-red-900/40"
                >
                  {showFullDetails ? "Hide" : "Show"} Technical Details
                </Button>

                {showFullDetails && (
                  <div className="mt-2 p-3 bg-red-100 rounded-md border border-red-200 dark:bg-red-900/40 dark:border-red-800">
                    {parsedError.hint && (
                      <p className="text-xs text-red-700 mb-2">
                        <strong>Hint:</strong> {parsedError.hint}
                      </p>
                    )}
                    {parsedError.details && (
                      <div className="text-xs text-red-700">
                        <strong>Details:</strong>
                        <pre className="mt-1 whitespace-pre-wrap font-mono text-xs bg-red-50 p-2 rounded border dark:bg-red-950/40">
                          {parsedError.details}
                        </pre>
                      </div>
                    )}
                    <div className="flex items-center gap-2 mt-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleCopyError}
                        className="text-xs text-red-600 hover:text-red-800 hover:bg-red-200 px-2 py-1 gap-1 dark:text-red-400 dark:hover:text-red-200 dark:hover:bg-red-900/50"
                      >
                        {copySuccess ? (
                          <>✓ Copied</>
                        ) : (
                          <>
                            <Copy size={12} />
                            Copy Error
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
